import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  ProjectsIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Listbox from '@/components/Listbox';
import { useProjects } from '@/features/app/api/get-projects';
import { useDebouncedCallback } from '@/hooks/use-debounce-callback';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { TFilterProps } from '@/types';

export function MentionFilterProjects({
  isOpen,
  isDark,
  showLabel,
  onToggleFilter,
  onClose,
}: TFilterProps) {
  const { t } = useTranslation();
  const { projects: state, setFilters: setState } = useProgramBroadcastFilters();
  const { data: projectsData = [] } = useProjects();

  const allProjects = useMemo(() => {
    return projectsData.map(state => ({
      value: state.id,
      label: state.name,
    }));
  }, [projectsData]);

  const handleChangeState = (value: string[]) => {
    setState({ projects: value });
  };

  const handleSearch = (search: string) => {
    setState({ projectSearch: search });
  };

  const debouncedSearch = useDebouncedCallback(handleSearch, 500);

  const ToolTipWrapper = ({ children }: { children: React.ReactNode }) => {
    if (showLabel) return <>{children}</>;
    return (
      <Tooltip help={t('Proyectos')} grow>
        {children}
      </Tooltip>
    );
  };

  return (
    <div className="relative w-full md:w-auto">
      <div
        onClick={onToggleFilter}
        className={clsx(
          'group flex w-full cursor-pointer items-center border-b-[1px] border-stroke-30-1 p-1',
          { 'border-text-medium-20': isOpen && allProjects.length !== 0 && !isDark },
          { 'hover:border-text-medium-20': !isDark },
          { 'border-text-neutral-10 hover:bg-reverse-opacity-05/10': isDark },
          { 'bg-reverse-opacity-05/10': isOpen && isDark }
        )}
      >
        <ToolTipWrapper>
          <ProjectsIcon
            className={clsx(
              'mr-1',
              {
                'text-text-medium-10': !isOpen && !isDark,
                'text-accent-active-primary-40': isOpen && !isDark,
              },
              {
                'text-text-neutral-10': isDark,
              },
              {
                'group-hover:text-accent-active-primary-40': !isDark,
              }
            )}
          />
        </ToolTipWrapper>
        {showLabel && (
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Medium}
            color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
            className={clsx(
              'select-none pr-size-4',
              {
                'text-text-medium-20': isOpen && !isDark,
              },
              {
                'group-hover:text-text-medium-20': !isDark,
              }
            )}
          >
            {t('Proyectos')}
          </Typography>
        )}
        {state.length > 0 && (
          <Badge
            variant={!isDark ? BadgeVariantEnum.DefaultFilled : BadgeVariantEnum.NeutralFilled}
            className="py-3"
          >
            + {state.length}
          </Badge>
        )}
      </div>
      {isOpen && (
        <div className="left-0 top-[120%] z-50 w-full bg-neutral-10 p-3 md:absolute md:min-w-[420px] md:shadow-full-medium">
          <Listbox
            dark={isDark}
            mode="multiple"
            items={allProjects}
            onChange={value => {
              handleChangeState(value);
            }}
            identifiers={{
              key: 'value',
              value: 'value',
              label: 'label',
              children: 'options',
            }}
            selected={state}
            onSearch={str => debouncedSearch(str)}
            onClose={onClose}
          />
        </div>
      )}
    </div>
  );
}
